import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const DEFAULTS = {
  site_title: "Sector Four · Soest Gaming League",
  favicon_url: "",
  logo_url: "",
  theme_color: "#00E5FF",
  meta_description: "Sector Four — das Racing Journal der Soest Gaming League. Rennberichte, News, Fahrer-Wertungen.",
  og_image: "",
  maintenance_mode: false,
  maintenance_message: "Wir basteln gerade an etwas Neuem.\n\nDie Seite ist gleich wieder da.",
  maintenance_start_at: "",
  maintenance_end_at: "",
  prelaunch_mode: false,
  prelaunch_eyebrow: "Pre-Launch",
  prelaunch_title: "Sector Four geht live",
  prelaunch_message: "Wir öffnen die Tore in Kürze. Stay tuned.",
  prelaunch_target: "",
  social_twitch_url: "",
  social_discord_url: "",
  social_youtube_url: "",
  social_twitter_url: "",
  social_instagram_url: "",
  countdown_enabled: false,
  countdown_title: "Saison 6 startet in",
  countdown_target: "",
  countdown_url: "",
  footer_tagline: "",
  footer_liga_text: "",
  ga_measurement_id: "",
  cookie_banner_script_url: "",
};

const isMaintenanceActive = (s) => {
  if (!s) return false;
  if (s.maintenance_mode) return true;
  const start = s.maintenance_start_at ? new Date(s.maintenance_start_at).getTime() : null;
  const end = s.maintenance_end_at ? new Date(s.maintenance_end_at).getTime() : null;
  const now = Date.now();
  if (start && end && !isNaN(start) && !isNaN(end)) return now >= start && now < end;
  if (start && !isNaN(start) && !end) return now >= start;
  return false;
};

// Pre-Launch: aktiv solange Toggle an UND Ziel-Zeit noch nicht erreicht.
// Sobald die Zeit erreicht ist, schaltet die Site automatisch frei (kein Backend-Eingriff nötig).
const isPrelaunchActive = (s) => {
  if (!s || !s.prelaunch_mode || !s.prelaunch_target) return false;
  const t = new Date(s.prelaunch_target).getTime();
  if (isNaN(t)) return false;
  return Date.now() < t;
};

const SiteSettingsContext = createContext({ settings: DEFAULTS, maintenance_active: false, prelaunch_active: false, refresh: () => {} });

export function SiteSettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => {
    api.get("/settings").then(({ data }) => {
      setSettings({ ...DEFAULTS, ...(data || {}) });
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  // Sekunden-Tick, damit `maintenance_active` und `prelaunch_active` automatisch umflippen.
  useEffect(() => {
    if (!settings.maintenance_start_at && !settings.maintenance_end_at && !settings.prelaunch_target) return;
    // Beim Pre-Launch alle Sekunde refreshen für genaue Auto-Deaktivierung
    const interval = settings.prelaunch_mode && settings.prelaunch_target ? 1000 : 30000;
    const i = setInterval(() => setTick(t => t + 1), interval);
    return () => clearInterval(i);
  }, [settings.maintenance_start_at, settings.maintenance_end_at, settings.prelaunch_target, settings.prelaunch_mode]);

  const maintenance_active = isMaintenanceActive(settings) || tick < 0; // tick als Dep
  const prelaunch_active = isPrelaunchActive(settings) || tick < 0;

  // Apply site-identity to <head>: title, favicon, theme-color, meta description, OG tags
  useEffect(() => {
    if (!settings) return;
    if (settings.site_title) document.title = settings.site_title;

    const upsertLink = (rel) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", rel);
        document.head.appendChild(link);
      }
      return link;
    };
    if (settings.favicon_url) {
      upsertLink("icon").setAttribute("href", settings.favicon_url);
      upsertLink("apple-touch-icon").setAttribute("href", settings.favicon_url);
    }
    const upsertMeta = (attr, key, value) => {
      let m = document.querySelector(`meta[${attr}="${key}"]`);
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute(attr, key);
        document.head.appendChild(m);
      }
      m.setAttribute("content", value || "");
    };
    upsertMeta("name", "theme-color", settings.theme_color || "#00E5FF");
    upsertMeta("name", "description", settings.meta_description || "");
    upsertMeta("property", "og:title", settings.site_title || "");
    upsertMeta("property", "og:description", settings.meta_description || "");
    if (settings.og_image) upsertMeta("property", "og:image", settings.og_image);
  }, [settings]);

  // Google Analytics 4: lädt gtag.js erst, wenn (a) eine Measurement-ID gesetzt ist
  // UND (b) der Nutzer im Cookie-Banner (CCM19, falls aktiv) zugestimmt hat.
  // Ohne aktiviertes Cookie-Banner wird GA wie bisher direkt geladen.
  useEffect(() => {
    const gaId = (settings.ga_measurement_id || "").trim();
    if (!gaId) return;
    const bannerActive = !!(settings.cookie_banner_script_url || "").trim();

    const loadGa = () => {
      if (window.__gaLoadedId === gaId) return;
      const loaderId = "ga-loader-script";
      let loader = document.getElementById(loaderId);
      if (!loader) {
        loader = document.createElement("script");
        loader.id = loaderId;
        loader.async = true;
        loader.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
        document.head.appendChild(loader);
      }
      window.dataLayer = window.dataLayer || [];
      function gtag(){ window.dataLayer.push(arguments); }
      window.gtag = gtag;
      gtag("js", new Date());
      gtag("config", gaId, { anonymize_ip: true });
      window.__gaLoadedId = gaId;
    };

    // Ohne Banner: direkt laden (alte Verhaltensweise)
    if (!bannerActive) {
      loadGa();
      return;
    }

    // Mit Banner: prüfen, ob CCM19 Consent für Analytics gegeben hat.
    // CCM19 stellt nach Banner-Init `window.CCM.acceptedEmbeddings` als Array bereit;
    // Items enthalten i. d. R. den Embedding-Namen (z. B. "Google Analytics").
    const hasAnalyticsConsent = () => {
      const ccm = window.CCM || window.ccm;
      if (!ccm) return false;
      const accepted = ccm.acceptedEmbeddings || ccm.embeddings || [];
      if (!Array.isArray(accepted)) return false;
      return accepted.some((e) => {
        const name = (typeof e === "string" ? e : (e?.name || e?.title || e?.id || "")) + "";
        const n = name.toLowerCase();
        return n.includes("google") || n.includes("analytic") || n.includes("gtag") || n === "ga4";
      });
    };

    const tryLoadIfConsented = () => {
      if (hasAnalyticsConsent()) loadGa();
    };

    // Initial-Check (für returning visitors mit gespeichertem Consent) +
    // CCM19-Events nach jedem Banner-Close/Update beachten.
    tryLoadIfConsented();
    const events = ["ccm19WidgetClosed", "CCM19Update", "ccm19_userConsent", "ccm19WidgetLoaded"];
    events.forEach((ev) => window.addEventListener(ev, tryLoadIfConsented));
    // Fallback: Poll für die ersten 30s, falls CCM-Events vor unserem Listener feuern.
    let pollCount = 0;
    const poll = setInterval(() => {
      pollCount += 1;
      tryLoadIfConsented();
      if (window.__gaLoadedId === gaId || pollCount > 60) clearInterval(poll);
    }, 500);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, tryLoadIfConsented));
      clearInterval(poll);
    };
  }, [settings.ga_measurement_id, settings.cookie_banner_script_url]);

  // Cookie-Consent-Banner (z. B. CCM19): lädt das konfigurierte Script auf jeder Seite.
  useEffect(() => {
    const url = (settings.cookie_banner_script_url || "").trim();
    if (!url) return;
    if (window.__cookieBannerLoadedUrl === url) return;
    const loaderId = "cookie-banner-loader";
    let loader = document.getElementById(loaderId);
    if (loader && loader.getAttribute("src") !== url) {
      loader.remove();
      loader = null;
    }
    if (!loader) {
      loader = document.createElement("script");
      loader.id = loaderId;
      loader.src = url;
      loader.setAttribute("referrerpolicy", "origin");
      document.head.appendChild(loader);
    }
    window.__cookieBannerLoadedUrl = url;
  }, [settings.cookie_banner_script_url]);

  return (
    <SiteSettingsContext.Provider value={{ settings, maintenance_active, prelaunch_active, refresh }}>
      {loaded ? children : (
        <div
          aria-busy="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "#0B0F19",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          data-testid="site-bootstrapping"
        >
          <div
            style={{
              width: 32,
              height: 32,
              border: "2px solid rgba(255,255,255,0.15)",
              borderTopColor: settings.theme_color || "#00E5FF",
              borderRadius: "50%",
              animation: "sfSpin 0.8s linear infinite",
            }}
          />
          <style>{`@keyframes sfSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings() {
  return useContext(SiteSettingsContext);
}
